import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import SearchIcon from "@mui/icons-material/Search";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { CanceledError } from "axios";
import Guid from "common/values/guid/guid";
import EntityMemberAPIService from "legal-entities/entities/entity-member/api/entity-member-api-service";
import LegalEntity from "legal-entities/entities/legal-entity/legal-entity";
import { debounce } from "lodash";
import Individual from "marketplace/entities/individual/individual";
import IndividualAvatar from "marketplace/view/individual-avatar";
import React, { useEffect } from "react";
import { useSession } from "users/session/session-context";

const PopoverContainer = styled("div")(({ theme }) => ({
  minWidth: "22rem",
  padding: theme.spacing(2),
  textAlign: "center",
}));
const SearchField = styled(TextField)(() => ({
  width: "100%",
})) as typeof TextField;
const LoaderProgress = styled(CircularProgress)(({ theme }) => ({
  display: "inline-block",
  marginBottom: theme.spacing(4),
  marginTop: theme.spacing(4),
}));
const ListContainer = styled(List)(() => ({
  maxHeight: "20rem",
  overflowX: "hidden",
  overflowY: "auto",
}));
const SelectedIcon = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));

type TeamLeaderSelectorProps = {
  teamLeaderUserId?: Guid;
  popoverAnchor?: HTMLDivElement;
  onTeamLeaderUpdated?: (userInfo?: Individual) => void;
  onPopoverClose?: () => void;
};

export default function TeamLeaderSelector(
  props: Readonly<TeamLeaderSelectorProps>
) {
  const {
    teamLeaderUserId,
    popoverAnchor,
    onTeamLeaderUpdated,
    onPopoverClose,
  } = props;

  const session = useSession();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [selectedTeamLeader, setSelectedTeamLeader] = React.useState<
    Individual | undefined
  >();
  const [coworkers, setCoworkers] = React.useState<Individual[]>([]);
  const [teamLeaderFilterResults, setTeamLeaderFilterResults] = React.useState<
    Individual[]
  >([]);
  const [searchTerm, setSearchTerm] = React.useState<string>("");

  useEffect(() => {
    let abortController = new AbortController();
    void getCoworkerList(abortController);
    return () => {
      abortController.abort();
      abortController = new AbortController();
    };
  }, []);

  useEffect(() => {
    const userProfileInfo = coworkers.find((c) =>
      c.userId?.isEqualTo(teamLeaderUserId)
    );
    setSelectedTeamLeader(userProfileInfo);
  }, [teamLeaderUserId, coworkers]);

  async function getCoworkerList(abortController: AbortController) {
    if (!session.user) return;

    setIsLoading(true);

    try {
      const entityInfo = session.currentEntity;
      const entity = new LegalEntity(
        entityInfo.entityId,
        entityInfo.entityName,
        entityInfo.entityClientFlag,
        entityInfo.entityVendorFlag
      );
      const entityMemberService = new EntityMemberAPIService(session);
      const coworkers = await entityMemberService.getLegalEntityMembersUserInfo(
        entity,
        abortController
      );

      const vendorReps = coworkers.filter(
        (coworker) =>
          (coworker.isAdmin || coworker.isOfficer) && coworker.userId
      )
      setCoworkers(coworkers.map((c) => c.asIndividual()));
      setTeamLeaderFilterResults(vendorReps.map((c) => c.asIndividual()));
    } catch (err) {
      if (err instanceof CanceledError) return;
      console.error(err);
    }
    setIsLoading(false);
  }

  const debouncedSearch = debounce(
    (value) =>
      setTeamLeaderFilterResults(
        coworkers.filter(
          (r) =>
            r.getFullName().toString().toLowerCase().includes(value.toLowerCase())
        )
      ),
    100
  );

  function search(value: string) {
    setSearchTerm(value);

    if (value === "") {
      setTeamLeaderFilterResults(coworkers);
      return;
    }

    debouncedSearch(value);
  }

  function saveChanges(vendorRep?: Individual) {
    onTeamLeaderUpdated?.(vendorRep);
    onPopoverClose?.();
  }

  return (
    <Popover
      anchorEl={popoverAnchor}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={popoverAnchor !== undefined}
      onClose={() => onPopoverClose?.()}
    >
      <PopoverContainer>
        <SearchField
          placeholder="Coworkers"
          slotProps={{
            input: {
              startAdornment: <SearchIcon />,
              endAdornment: (
                <>
                  {searchTerm.length > 0 && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchTerm("");
                        search("");
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </>
              ),
            },
          }}
          value={searchTerm}
          onChange={(event) => search(event.target.value)}
        />
        {isLoading && <LoaderProgress size={50} disableShrink />}
        {!isLoading && (
          <ListContainer dense>
            {teamLeaderFilterResults.length < 1 && (
              <ListItem>
                <ListItemText>
                  {`No coworkers matching: ${searchTerm}`}
                </ListItemText>
              </ListItem>
            )}
            {teamLeaderFilterResults.length > 0 &&
              teamLeaderFilterResults.map((vendorRep: Individual) => {
                const isSelected = selectedTeamLeader?.userId?.isEqualTo(
                  vendorRep.userId
                );

                return (
                  <ListItemButton
                    disableGutters
                    key={vendorRep.userId?.value}
                    selected={isSelected}
                    onClick={() => saveChanges(vendorRep)}
                  >
                    <ListItemAvatar>
                      {isSelected ? (
                        <SelectedIcon>
                          <DoneIcon />
                        </SelectedIcon>
                      ) : (
                        <IndividualAvatar
                          avatarId={vendorRep.profile?.avatarId}
                          individualId={vendorRep.id}
                          session={session}
                        />
                      )}
                    </ListItemAvatar>
                    <ListItemText
                      primary={vendorRep.getFullName()?.toString()}
                      secondary={<>{vendorRep.profile?.email?.toString()}</>}
                    />
                  </ListItemButton>
                );
              })}
          </ListContainer>
        )}
      </PopoverContainer>
    </Popover>
  );
}
